import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CpqConnectionTestResult,
  CpqLegacyImportCandidatesResult,
  CpqLegacyImportPreview,
  CpqLegacyImportRunResult,
  CpqQuoteListResult,
  CpqSettings,
  CpqStatus,
  UpdateCpqSettingsInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const cpqKeys = {
  status: ['cpq', 'status'] as const,
  settings: ['cpq', 'settings'] as const,
  quotes: ['cpq', 'quotes'] as const,
};

/**
 * Whether the CPQ integration is switched on. Readable by any signed-in member (the
 * full settings are admin-only), so the opportunity form can hide its CPQ tab.
 */
export function useCpqStatus(): UseQueryResult<CpqStatus> {
  return useQuery({
    queryKey: cpqKeys.status,
    staleTime: 60_000,
    queryFn: () => api.get<CpqStatus>('/connectwise-cpq/status'),
  });
}

export function useCpqSettings(): UseQueryResult<CpqSettings> {
  return useQuery({
    queryKey: cpqKeys.settings,
    queryFn: () => api.get<CpqSettings>('/connectwise-cpq/settings'),
  });
}

export function useUpdateCpqSettings(): UseMutationResult<
  CpqSettings,
  unknown,
  UpdateCpqSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCpqSettingsInput) =>
      api.put<CpqSettings>('/connectwise-cpq/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cpqKeys.status });
      void queryClient.invalidateQueries({ queryKey: cpqKeys.settings });
      void queryClient.invalidateQueries({ queryKey: cpqKeys.quotes });
    },
  });
}

export function useTestCpqConnection(): UseMutationResult<CpqConnectionTestResult, unknown, void> {
  return useMutation({
    mutationFn: () => api.post<CpqConnectionTestResult>('/connectwise-cpq/settings/test'),
  });
}

export function useCpqQuotes(enabled: boolean): UseQueryResult<CpqQuoteListResult> {
  return useQuery({
    queryKey: cpqKeys.quotes,
    enabled,
    queryFn: () => api.get<CpqQuoteListResult>('/connectwise-cpq/quotes?pageSize=50'),
  });
}

/** Scans all CPQ quotes for legacy-import candidates (admin, read-only). */
export function useCpqLegacyImportCandidates(): UseMutationResult<
  CpqLegacyImportCandidatesResult,
  unknown,
  void
> {
  return useMutation({
    mutationFn: () =>
      api.post<CpqLegacyImportCandidatesResult>('/connectwise-cpq/legacy-import/candidates'),
  });
}

/** Previews what the legacy import would create for the given quote numbers. */
export function useCpqLegacyImportPreview(): UseMutationResult<
  CpqLegacyImportPreview[],
  unknown,
  number[]
> {
  return useMutation({
    mutationFn: (quoteNumbers: number[]) =>
      api.post<CpqLegacyImportPreview[]>('/connectwise-cpq/legacy-import/preview', {
        quoteNumbers,
      }),
  });
}

/** Imports the given legacy CPQ quotes as PSA quotes (call in small batches). */
export function useCpqLegacyImportRun(): UseMutationResult<
  CpqLegacyImportRunResult,
  unknown,
  number[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quoteNumbers: number[]) =>
      api.post<CpqLegacyImportRunResult>('/connectwise-cpq/legacy-import/run', { quoteNumbers }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });
}
